<template>
  <v-container class="pa-0" fluid>
    <v-row>
      <v-col cols="12">
        <!-- Header with title and actions menu -->
        <div class="d-flex align-center mb-2">
          <div class="flex-grow-1">
            <div class="text-caption text--secondary">{{ category }}</div>
            <div class="text-h6">{{ event.code | codeAsString }}</div>
          </div>
          <div class="flex-shrink-0">
            <slot name="actions-menu"></slot>
          </div>
        </div>

        <!-- Expandable info -->
        <div v-if="event.details?.info" class="text-caption mb-2">
          <div v-if="!isInfoLong">
            {{ event.details?.info }}
          </div>
          <div v-else>
            <div v-if="!showFullInfo">
              {{ truncatedInfo }}
              <v-btn text x-small color="primary" class="pa-0 ml-1" style="min-width: auto; height: auto;"
                @click="showFullInfo = true">
                więcej
                <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
              </v-btn>
            </div>
            <div v-else>
              {{ event.details?.info }}
              <v-btn text x-small color="primary" class="pa-0 ml-1" style="min-width: auto; height: auto;"
                @click="showFullInfo = false">
                mniej
                <v-icon x-small class="ml-1">mdi-chevron-up</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- <div class="text-caption font-weight-medium">{{ event.details.info }}</div> -->
        <v-row class="mt-2" v-if="costsCount > 0 || notesCount > 0">
          <v-col cols="12" class="d-flex flex-wrap align-center">
            <cost-chip v-for="(cost, index) in costs" :key="index" :color="cost.color" :text-color="cost.textColor"
              :name="cost.name" chip-class="mr-1 mb-1" small />

            <!-- Separator between costs and notes -->
            <v-divider v-if="notesCount > 0" vertical class="mx-2" style="height: 24px;"></v-divider>

            <!-- Notes chip -->
            <v-chip v-if="notesCount > 0" small color="info" text-color="white" class="mr-1 mb-1 notes-chip"
              @click="$emit('show-notes')">
              <v-icon small class="mr-1" color="white">mdi-note-text</v-icon>
              Notatki ({{ notesCount }})
            </v-chip>
          </v-col>
        </v-row>
        <v-divider class="my-2" />
        <div>
          <v-row>
            <v-col cols="6">
              <div class="text-caption text--secondary">ID:</div>
              <div class="font-weight-medium">{{ event.id ? '#' + event.id : '-' }}</div>
            </v-col>
            <v-col cols="6">
              <span class="text-caption text--secondary">Czas:</span>
              <div class="font-weight-medium">{{ event.created_at | dateMediumTimeShort }}</div>
            </v-col>
          </v-row>
          <v-divider class="my-2" />
          <v-row v-if="false">
            <v-col cols="6">
              <div class="text-caption text--secondary">Typ:</div>
              <div class="font-weight-medium">{{ category || '-' }}</div>
            </v-col>
            <v-col cols="6">
              <span class="text-caption text--secondary">Status:</span>
              <div class="font-weight-medium">{{ statusText }}</div>
            </v-col>
            <v-divider class="my-2" />
          </v-row>

          <div class="mb-2">
            <span class="text-caption text--secondary">Lokalizacja:</span>
            <div class="font-weight-medium">
              <router-link v-if="siteRoute" :to="siteRoute" class="text-link">
                {{ event.site | codeName }} {{ event.site.name }}
              </router-link>
              <span v-else>{{ event.site | codeName }} {{ event.site.name }}</span>
            </div>
            <div class="text-caption font-weight-medium">
              <router-link v-if="siteLocationRoute" :to="siteLocationRoute" class="text-link">
                {{ event.site?.city || '-' }}, {{ event.site?.address || '-' }}
              </router-link>
              <span v-else>{{ event.site?.city || '-' }}, {{ event.site?.address || '-' }}</span>
            </div>
          </div>
          <v-divider class="my-2" />

          <!-- Reporting person section -->
          <div class="mb-2" v-if="reportingPerson">
            <span class="text-caption text--secondary">Zgłaszający:</span>
            <div class="font-weight-medium d-flex align-center">
              <v-icon small class="mr-2" color="primary">mdi-account-outline</v-icon>
              {{ reportingPerson }}
            </div>
          </div>
          <v-divider class="my-2" v-if="reportingPerson" />
          <!-- Created by section -->
          <v-row v-if="createdBy || !isEventForMe">
            <v-col cols="6" class="" v-if="createdBy">
              <span class="text-caption text--secondary">Utworzone przez:</span>
              <div class="font-weight-medium d-flex align-center">
                <v-icon small class="mr-2" color="primary">mdi-account-outline</v-icon>
                {{ createdBy | userName}}
              </div>
            </v-col>
            <v-col cols="6" class="" v-if="!isEventForMe">
              <span class="text-caption text--secondary">Przypisane do:</span>
              <div class="font-weight-medium d-flex align-center">
                <v-icon small class="mr-2" color="primary">mdi-account-outline</v-icon>
                {{ firstAssigneeUser | userName }}
              </div>
            </v-col>
          </v-row>
          <v-divider class="my-2" v-if="createdBy || isEventForMe" />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Categories, getRootTask } from '@/store/modules/event';
import CostChip from '@/components/CostChip.vue';
import { firstAssigneeUser, isUserAssignedToTask } from '../../store/modules/task';
import { sync } from 'vuex-pathify'

export default {
  name: 'EventDetails',
  components: {
    CostChip
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    notes: {
      type: Array,
      default: () => []
    },
    costs: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showFullInfo: false
    }
  },
  computed: {
    user: sync('user.profile'),
    notesCount() {
      return this.notes?.length || 0
    },
    costsCount() {
      return this.costs?.length || 0
    },
    category() {
      return Categories.get(this.event.code?.category)?.name || '-'
    },
    statusText() {
      // Try to get status from dispatcherTask or event.details.status
      if (this.event.dispatcherTask?.details?.status?.message) {
        return this.event.dispatcherTask.details.status.message
      }
      if (this.event.details?.status) {
        return this.event.details.status
      }
      return '-'
    },
    eventColor() {
      return Categories.get(this.event.code?.category)?.color || ''
    },
    onEventColor() {
      return Categories.get(this.event.code?.category)?.onColor || ''
    },
    categoryIcon() {
      return Categories.get(this.event.code?.category)?.icon || 'mdi-alert-circle-outline'
    },
    reportingPerson() {
      // Try to get reporting person from event.details.reported_by
      if (this.event.details?.reported_by?.full_name) {
        return this.event.details.reported_by.full_name
      }
      return null
    },
    firstAssigneeUser() {
      return firstAssigneeUser(getRootTask(this.event))
    },
    isEventForMe() {
      return isUserAssignedToTask(getRootTask(this.event), this.user.id)
    },
    createdBy() {
      return this.event.details?.created_by
    },
    isFirstAssigneeIsSameCreator() {
      const firstAssignee = this.firstAssigneeUser
      const creator = this.createdBy
      return firstAssignee && creator && (firstAssignee.id === creator.id)
    },

    isInfoLong() {
      const info = this.event.details?.info || ''
      return info.length > 50 // Consider info long if more than 50 characters
    },
    truncatedInfo() {
      const info = this.event.details?.info || ''
      if (info.length <= 50) return info
      return info.substring(0, 50) + '...'
    },
    siteRoute() {
      const siteId = this.event.site?.id
      if (!siteId) return null

      return {
        name: 'Site',
        params: { siteId: String(siteId) }
      }
    },
    siteLocationRoute() {
      if (!this.siteRoute) return null

      return {
        ...this.siteRoute,
        hash: '#location'
      }
    }
  }
}
</script>

<style scoped>
.notes-chip {
  cursor: pointer;
}

.notes-chip:hover {
  opacity: 0.8;
}
</style>
